import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { useEffect } from 'react';

import useAuthor from '../../hooks/useAuthor';
import useFiltersQuery from '../../hooks/useFiltersQuery';
import useScrollEnter from '../../hooks/useScrollEnter';
import { useSubscription } from '../../hooks/useSubscription';
import { usePageViewTracking } from '../../hooks/useTracking';
import { useAuthorUrlGenerator } from '../../hooks/useUrlGenerator';

import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useSubscriptions } from '../../contexts/SiteContext';
import HeaderAd from '../ads/HeaderAd';
import AuthorDefaultHeader from '../headers/AuthorDefaultHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import SortMenu from '../menus/SortMenu';
import NotFoundPage from './NotFoundPage';

import styles from '../../styles/pages/author-page.module.css';

type AuthorPageProps = {
    slug: string | null;
    current?: boolean;
    subscribe?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: ((...args: unknown[]) => void) | null;
    onLeave?: ((...args: unknown[]) => void) | null;
};

function AuthorPage({
    slug,
    current = false,
    subscribe: shouldSubscribe = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: AuthorPageProps) {
    const url = useAuthorUrlGenerator();
    const { author = null, notFound = false } = useAuthor(slug);
    const { id: authorId = null } = author || {};
    const user = useUser();
    const isCheckingAuth = useIsCheckingAuth();
    const subscriptions = useSubscriptions();
    const subscription =
        authorId !== null
            ? subscriptions.find(
                  ({ author = null }) => author !== null && author.id === authorId,
              ) || null
            : null;
    const { subscribed = false, subscribe } = useSubscription({
        subscription: subscription !== null ? subscription.handle : null,
    });
    const filters = useFiltersQuery();

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    usePageViewTracking(current && author !== null, {
        pageType: 'author',
    });

    useEffect(() => {
        if (shouldSubscribe && current && user !== null && !subscribed && subscription !== null) {
            subscribe();
        }
    }, [shouldSubscribe, isCheckingAuth]);

    if (notFound) {
        return <NotFoundPage current={current} />;
    }

    return (
        <div className={classNames([styles.container, className, contentClassName])}>
            <HeaderAd />
            <AuthorDefaultHeader
                {...author}
                placeholder={author === null}
                className={styles.header}
            />
            <SortMenu
                url={
                    url({
                        slug,
                    }) || '#'
                }
                className={styles.filters}
            />
            <main>
                <InfiniteItemsBlocks
                    query={{
                        author: authorId,
                        ...filters,
                    }}
                    cardTheme="box-translucent-dark"
                    cardWith={['brand-icon']}
                    disabled={authorId === null}
                />
            </main>
        </div>
    );
}

export default AuthorPage;
